import { useEffect, useRef, useState, useCallback } from 'react'
import { WS_BASE_URL } from '../config'

// Push-only socket: the backend sends 'appointment_created' when someone
// books a visit on a listing you own, and 'appointment_updated' whenever an
// appointment's status or time changes (accept/decline/reschedule/complete).
// Mirrors usePostsWebSocket's reconnect skeleton for consistency.
const useAppointmentsWebSocket = (isAuthenticated, onMessage) => {
  const [wsConnected, setWsConnected] = useState(false)
  const wsRef = useRef(null)
  const reconnectTimeoutRef = useRef(null)
  const reconnectAttempts = useRef(0)
  const maxReconnectAttempts = 5
  const isConnectingRef = useRef(false)

  const onMessageRef = useRef(onMessage)
  useEffect(() => { onMessageRef.current = onMessage }, [onMessage])

  const connect = useCallback(() => {
    if (!isAuthenticated) return
    if (isConnectingRef.current) return
    if (wsRef.current?.readyState === WebSocket.OPEN) return

    // Authenticate with the same token used for REST calls, not the
    // session cookie — the session rotates on every login (anywhere in the
    // browser) while this token doesn't, so this is what stays valid for
    // as long as the frontend considers itself logged in.
    const token = localStorage.getItem('auth_token')
    if (!token) return

    isConnectingRef.current = true

    const ws = new WebSocket(`${WS_BASE_URL}/ws/appointments/?token=${encodeURIComponent(token)}`)
    wsRef.current = ws

    ws.onopen = () => {
      setWsConnected(true)
      reconnectAttempts.current = 0
      isConnectingRef.current = false
    }

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data)
        if (['appointment_created', 'appointment_updated'].includes(message.type)) {
          if (message.data) onMessageRef.current(message.type, message.data)
        } else if (message.type === 'error') {
          console.error('WebSocket error from server:', message.message)
        }
      } catch (err) {
        console.error('Error parsing WebSocket message:', err)
      }
    }

    ws.onerror = () => {
      setWsConnected(false)
      isConnectingRef.current = false
    }

    ws.onclose = () => {
      setWsConnected(false)
      isConnectingRef.current = false
      if (reconnectAttempts.current < maxReconnectAttempts) {
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000)
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectAttempts.current += 1
          connect()
        }, delay)
      }
    }
  }, [isAuthenticated]) // onMessage intentionally excluded — handled via ref

  useEffect(() => {
    connect()
    return () => {
      isConnectingRef.current = false
      clearTimeout(reconnectTimeoutRef.current)
      if (wsRef.current) {
        wsRef.current.onclose = null
        if (wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.close()
        }
      }
    }
  }, [connect])

  return { wsConnected }
}

export default useAppointmentsWebSocket
